import { useState, useEffect, useCallback } from 'react'

// 🚀 Constante global declarada fuera del componente
const API_URL = process.env.REACT_APP_API_URL

export default function ListaExclusion({ toast, comercioId }) {
  const [numeros, setNumeros] = useState([])
  const [telefono, setTelefono] = useState('')
  const [descripcion, setDescripcion] = useState('')
  const [cargando, setCargando] = useState(false)
  
  // Estados para el modal moderno
  const [modalVisible, setModalVisible] = useState(false)
  const [numeroAEliminar, setNumeroAEliminar] = useState(null)

  // 1. Cargar lista (GET)
  const cargarNumeros = useCallback(async () => {
    if (!comercioId) return
    try {
      const idNum = parseInt(comercioId, 10)
      const res = await fetch(`${API_URL}/api/numeros-excluidos/${idNum}`)
      if (!res.ok) throw new Error("Error HTTP")
      const data = await res.json()
      setNumeros(data)
    } catch (error) {
      console.error("Error al cargar la lista de exclusión:", error)
    }
  }, [comercioId])

  useEffect(() => {
    cargarNumeros()
  }, [cargarNumeros])

  // 2. Agregar número (POST)
  const handleAgregar = async (e) => {
    e.preventDefault()
    if (!telefono) return

    setCargando(true)
    try {
      const idNum = parseInt(comercioId, 10)
      const numeroCompleto = `549${telefono}`

      const res = await fetch(`${API_URL}/api/numeros-excluidos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          comercio_id: idNum,
          telefono: numeroCompleto,
          descripcion: descripcion
        })
      })

      if (res.ok) {
        setTelefono('')
        setDescripcion('')
        toast('Número excluido correctamente', 'success')
        cargarNumeros()
      } else {
        throw new Error()
      }
    } catch (error) {
      toast('No se pudo agregar el número', 'error')
    } finally {
      setCargando(false)
    }
  }

  // 3. Preparar eliminación (Abre modal)
  const confirmarEliminacion = (numero) => {
    setNumeroAEliminar(numero)
    setModalVisible(true)
  }

  // 4. Ejecutar eliminación (DELETE)
  const ejecutarEliminacion = async () => {
    if (!numeroAEliminar) return

    try {
      const res = await fetch(`${API_URL}/api/numeros-excluidos/${numeroAEliminar.id}`, {
        method: 'DELETE'
      })

      if (res.ok) {
        toast('Número habilitado nuevamente', 'success')
        cargarNumeros()
      } else {
        throw new Error()
      }
    } catch (error) {
      toast('Error al habilitar el número', 'error')
    } finally {
      setModalVisible(false)
      setNumeroAEliminar(null)
    }
  }

  // Cerrar modal sin hacer nada
  const cancelarEliminacion = () => {
    setModalVisible(false)
    setNumeroAEliminar(null)
  }

  return (
    <>
      <section className="config-card" style={{ marginTop: '1.25rem' }}>
        {/* Ajuste de márgenes para acercar el texto al título */}
        <h3 className="config-card-title" style={{ margin: '0 0 4px 0' }}>Lista de exclusión (Contactos ignorados)</h3>
        <p className="config-help-text" style={{ margin: '0 0 1.25rem 0' }}>
          Agregá números de familiares, tu propio celular o socios. El bot no procesará mensajes ni consumirá créditos si interactúan con la línea del local.
        </p>

        {/* Formulario interno */}
        <div className="config-internal-grid grid-compact" style={{ marginBottom: '1.5rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '1.5rem' }}>
          
          <div className="config-form-group mb-0">
            <label className="config-label">Número de Teléfono</label>
            <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'hidden', background: 'var(--bg-card)' }}>
              {/* Se agregó whiteSpace: 'nowrap' para que el +54 9 no se divida */}
              <span style={{ whiteSpace: 'nowrap', padding: '0 12px', color: '#64748b', fontWeight: '500', borderRight: '1px solid var(--border-color)', backgroundColor: '#f8fafc', height: '42px', display: 'flex', alignItems: 'center' }}>
                +54 9
              </span>
              <input 
                type="text" 
                style={{ flex: 1, border: 'none', height: '42px', padding: '0 12px', outline: 'none', background: 'transparent', fontSize: '14px', color: 'var(--text-main)' }}
                placeholder="Ej: 2494600176" 
                value={telefono}
                onChange={(e) => setTelefono(e.target.value.replace(/\D/g, ''))}
              />
            </div>
          </div>

          <div className="config-form-group mb-0">
            <label className="config-label">Nombre / Descripción</label>
            <input 
              type="text" 
              className="config-input"
              placeholder="Ej: Mi celular personal / Mamá" 
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
            />
          </div>

          {/* Botón con color llamativo (azul) para que no pase desapercibido */}
          <div className="config-form-group span-full mb-0" style={{ marginTop: '0.5rem' }}>
            <button 
              type="button" 
              onClick={handleAgregar} 
              disabled={cargando} 
              className="btn-exclusion"
              style={{
                backgroundColor: '#2563eb',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 20px',
                fontWeight: '600',
                cursor: cargando ? 'not-allowed' : 'pointer',
                opacity: cargando ? 0.7 : 1,
                transition: 'background-color 0.2s',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {cargando ? 'Guardando...' : 'Excluir número'}
            </button>
          </div>
        </div>

        {/* Tabla de registros */}
        <div className="config-form-group mb-0">
          <label className="config-label">Números actualmente ignorados</label>
          {numeros.length === 0 ? (
            <p className="config-help-text" style={{ fontStyle: 'italic' }}>No hay números excluidos en este comercio.</p>
          ) : (
            <div style={{ overflowX: 'auto', marginTop: '0.5rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border-color)', textAlign: 'left', color: '#64748b' }}>
                    <th style={{ padding: '0.5rem' }}>Teléfono</th>
                    <th style={{ padding: '0.5rem' }}>Descripción</th>
                    <th style={{ padding: '0.5rem', textAlign: 'right' }}>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {numeros.map((num) => (
                    <tr key={num.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '0.75rem 0.5rem', fontWeight: '500' }}>+{num.telefono}</td>
                      <td style={{ padding: '0.75rem 0.5rem', color: 'var(--text-muted)' }}>{num.descripcion || 'Sin detalle'}</td>
                      <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>
                        
                        <button 
                          onClick={() => confirmarEliminacion(num)} 
                          title="Habilitar bot para este número"
                          style={{ 
                            background: '#fee2e2', 
                            border: 'none', 
                            color: '#ef4444', 
                            cursor: 'pointer', 
                            padding: '6px', 
                            borderRadius: '6px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#fecaca'}
                          onMouseLeave={(e) => e.currentTarget.style.background = '#fee2e2'}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 6h18"></path>
                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                          </svg>
                        </button>

                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* Modal Moderno para Confirmar Eliminación */}
      {modalVisible && (
        <div style={{ 
          position: 'fixed', 
          top: 0, left: 0, right: 0, bottom: 0, 
          backgroundColor: 'rgba(15, 23, 42, 0.6)', 
          backdropFilter: 'blur(3px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', 
          zIndex: 9999 
        }}>
          <div className="animate-fade-in" style={{ 
            background: 'var(--bg-card)', 
            borderRadius: '12px', 
            padding: '1.5rem', 
            width: '90%', 
            maxWidth: '400px', 
            border: '1px solid var(--border-color)',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' 
          }}>
            <h4 style={{ margin: '0 0 10px 0', fontSize: '18px', color: 'var(--text-main)', fontWeight: '600' }}>
              Habilitar asistente virtual
            </h4>
            <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: 'var(--text-muted)', lineHeight: '1.5' }}>
              ¿Estás seguro que querés remover el número <strong style={{ color: 'var(--text-main)' }}>+{numeroAEliminar?.telefono}</strong> de la lista de exclusión? El bot volverá a responder a sus mensajes.
            </p>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button 
                onClick={cancelarEliminacion}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border-color)',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  color: 'var(--text-muted)',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button 
                onClick={ejecutarEliminacion}
                style={{
                  background: '#ef4444',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                }}
              >
                Sí, habilitar bot
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}